/**
 * AR Write-Offs Query Hooks
 *
 * Custom hooks for:
 * - Fetching AR Write-Offs using TanStack React Query
 * - Managing write-off mutations (approve/reject/update)
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { isEqual, reject, map, compact, get } from 'lodash';

import { showToast } from '@repo/ui';
import { QueryStringParams, PaginatedResponse } from '@xtnd/web/shared/types';
import { queryClient } from '@xtnd/web/providers';
import { WriteOffCategory, WriteOffStatus } from '@repo/types';
import { useQueryKeyStore, StableQueryKey } from '@xtnd/web/shared/stores';

import { reportingWriteOffsApi } from '../api/reportingWriteOffsApi';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import {
  UpdateWriteOffPayload,
  WriteOff,
  WriteOffGroupByClient,
  WriteOffItem,
} from '../types/writeOffs';

export const WRITE_OFFS_QUERY_KEYS = {
  all: ['write-offs'] as const,
  list: (params: QueryStringParams) => ['write-offs', params] as const,
  items: (id: string) => ['write-offs', id] as const,
  single: (id: string) => ['write-off', id] as const,
};

/**
 * Hook for fetching write-offs with pagination and filtering
 */
/**
 * Hook for creating a pending CUTS_APPLIED write-off for an Appeal Denied invoice.
 */
export function useCreateCutsAppliedWriteOff() {
  return useMutation<
    WriteOffItem,
    Error,
    { invoiceId: string; appealId: string; feesCut: number; expensesCut: number; comment?: string }
  >({
    mutationFn: async ({ invoiceId, appealId, feesCut, expensesCut, comment }) => {
      const response = await reportingWriteOffsApi.createCutsAppliedWriteOff(invoiceId, appealId, {
        feesCut,
        expensesCut,
        comment,
      });
      return response.data;
    },
    onSuccess: async (_, { invoiceId }) => {
      await queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] });
      await queryClient.invalidateQueries({ queryKey: WRITE_OFFS_QUERY_KEYS.items(invoiceId) });
      await queryClient.invalidateQueries({ queryKey: WRITE_OFFS_QUERY_KEYS.all });
    },
  });
}

export function useWriteOffs(params: QueryStringParams) {
  const { getQueryKey, setQueryKey } = useQueryKeyStore();
  const queryKey = WRITE_OFFS_QUERY_KEYS.list(params);

  // Update query key store only when the query key actually changes
  const queryKeyStore = getQueryKey('ar-collections', 'reporting.write-off');
  if (!isEqual(queryKeyStore, queryKey)) {
    setQueryKey('ar-collections', 'reporting.write-off', queryKey as unknown as StableQueryKey);
  }

  return useQuery({
    queryKey,
    queryFn: () => reportingWriteOffsApi.getWriteOffs(params),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook for fetching lightweight write-offs summary counts by status.
 */
export function useWriteOffItems(invoiceId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: WRITE_OFFS_QUERY_KEYS.items(invoiceId),
    queryFn: () => reportingWriteOffsApi.getWriteOffsItems(invoiceId),
    enabled: (options?.enabled ?? true) && !!invoiceId,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook for fetching a single write-off by ID.
 */
export function useGetWriteOff(writeOffId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: WRITE_OFFS_QUERY_KEYS.single(writeOffId),
    queryFn: async () => {
      const response = await reportingWriteOffsApi.getWriteOff(writeOffId);
      return response.data;
    },
    enabled: options?.enabled ?? !!writeOffId,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook for patching a write-off (update/approve/reject)
 *
 * Implements optimistic updates with cache management similar to useUpdateAppealStatus.
 * Handles both status changes (which move items between tabs) and note-only updates.
 *
 * @param queryParams - Current query parameters for building cache keys
 * @param isAdmin - Whether the current user is an admin
 * @param onLocalStateUpdate - Optional callback for updating local component state (e.g., grouped view collapse)
 *
 * Features:
 * - Optimistic removal from source tab on status change
 * - Automatic cache invalidation on success
 * - Rollback on error
 * - Toast notifications for all operations
 */
export function useUpdateWriteOff(
  queryParams?: QueryStringParams,
  isAdmin: boolean = false,
  onLocalStateUpdate?: (writeOffId: string) => void,
  onLocalCommentUpdate?: (writeOffId: string, comment?: string) => void
) {
  return useMutation<
    WriteOffItem,
    Error,
    UpdateWriteOffPayload,
    { previousData: unknown; queryKey: readonly unknown[]; invoiceId: string } | undefined
  >({
    mutationFn: async (payload: UpdateWriteOffPayload) => {
      const response = await reportingWriteOffsApi.patchWriteOff(payload, isAdmin);
      return response.data;
    },

    // Optimistic update - remove from current list immediately
    onMutate: async payload => {
      if (!queryParams) return undefined;

      const queryKey = WRITE_OFFS_QUERY_KEYS.list(queryParams);
      const invoiceQueryKey = WRITE_OFFS_QUERY_KEYS.items(payload.invoiceId);

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value for rollback
      const previousData = queryClient.getQueryData(queryKey);
      const previousInvoiceData =
        queryClient.getQueryData<PaginatedResponse<WriteOffItem>>(invoiceQueryKey);
      const isOnlyItem = previousInvoiceData?.data.length === 1;

      // Only do optimistic removal for status changes (not note-only updates)
      if (payload.status) {
        if (previousInvoiceData) {
          if (isOnlyItem) {
            queryClient.removeQueries({ queryKey: invoiceQueryKey });
          } else {
            queryClient.setQueryData(
              invoiceQueryKey,
              previousInvoiceData.data.filter(
                (writeOff: WriteOffItem) => writeOff.writeOffId !== payload.writeOffId
              )
            );
          }
        }

        queryClient.setQueryData(queryKey, (old: unknown) => {
          const oldData = old as PaginatedResponse<WriteOff | WriteOffGroupByClient> | undefined;
          if (!oldData?.data) return old;
          if (!isOnlyItem) return old;
          // Check if data is grouped by client (has 'invoices' property)
          let numOfInvoices: number = get(oldData.data, 'numOfInvoices', 0);

          if (numOfInvoices) {
            numOfInvoices--;
            // Remove invoice from grouped structure
            const groupedData = oldData.data as WriteOffGroupByClient[];
            const updatedData = compact(
              map(groupedData, group => {
                return numOfInvoices > 0 ? { ...group, numOfInvoices } : null;
              })
            );

            return {
              ...oldData,
              data: updatedData,
              pagination: oldData.pagination
                ? {
                    ...oldData.pagination,
                    total: Math.max((oldData.pagination.total ?? 1) - 1, 0),
                  }
                : undefined,
            };
          }

          // Handle flat data (no grouping)
          const flatData = oldData.data as WriteOff[];
          const updatedData = reject(
            flatData,
            (inv: WriteOff) => inv.invoiceId === payload.invoiceId
          );
          return {
            ...oldData,
            data: updatedData,
            pagination: oldData.pagination
              ? {
                  ...oldData.pagination,
                  total: Math.max((oldData.pagination.total ?? 1) - 1, 0),
                }
              : undefined,
          };
        });
      }

      return { previousData, queryKey, invoiceId: payload.writeOffId };
    },

    onSuccess: async (data, payload) => {
      // Update local state for grouped view collapse (only for status changes)
      if (payload.status && onLocalStateUpdate) {
        onLocalStateUpdate(payload.writeOffId);
      }

      // Invalidate activity logs cache for this invoice (so ActivityDrawer shows fresh data)
      await queryClient.invalidateQueries({
        queryKey: ['activityLogs', 'invoice', payload.writeOffId],
      });

      // Show appropriate toast based on operation type
      if (payload.status) {
        // Invalidate all write-off queries to ensure consistency and auto-reload the list
        await queryClient.invalidateQueries({ queryKey: WRITE_OFFS_QUERY_KEYS.all });

        switch (data.status) {
          case WriteOffStatus.APPROVED: {
            const category =
              data.category === WriteOffCategory.WRITE_OFF ? 'Write Off' : 'Bad Debt';
            showToast.success(`Write Off has been approved as ${category}`);
            break;
          }
          case WriteOffStatus.REJECTED:
            showToast.success(`Write Off has been marked as rejected.`);
            break;
          case WriteOffStatus.ARCHIVED:
            showToast.success(`Write Off has been marked as processed and archived`);
            break;
          default:
            showToast.success(`Write Off has been updated.`);
        }
      } else {
        // Update cache and local state after successful comment update
        const itemsQueryKey = WRITE_OFFS_QUERY_KEYS.items(payload.invoiceId);
        queryClient.setQueryData(itemsQueryKey, (old: unknown) => {
          const oldData = old as PaginatedResponse<WriteOffItem> | undefined;
          if (!oldData?.data) return old;

          return {
            ...oldData,
            data: map(oldData.data, (item: WriteOffItem) =>
              item.writeOffId === payload.writeOffId ? { ...item, ...data } : item
            ),
          };
        });

        // Update local state after success (for grouped view)
        if (onLocalCommentUpdate) {
          onLocalCommentUpdate(payload.writeOffId, payload.comment);
        }

        showToast.success(`Comment added to write off`);
      }
    },

    onError: (error, _payload, context) => {
      // Rollback cache to previous data on error (for status changes)
      if (context?.previousData && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
      showToast.error(getApiErrorMessage(error, 'Failed to update write-off. Please try again.'));
    },
  });
}
